import { useMemo, useState } from 'react'
import { AlertTriangle, Lock, Plus } from 'lucide-react'
import {
  EmptyState, ErrorState, FilterBar, FormDialog, PageHeader,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useRoleMatrix, useSysAdminMutations } from '../api'

/**
 * Every permission by module, against every role.
 *
 * <p>Two things the matrix does that a list of checkboxes does not: it refuses to edit
 * SUPER_ADMIN — the role the permission catalogue is verified against at startup — and it
 * names the segregation-of-duties pairs a role holds both sides of while somebody is editing
 * it, rather than at the moment somebody uses the pair.
 */
export function RolesPage() {
  useBreadcrumb()
  const matrix = useRoleMatrix()
  const actions = useSysAdminMutations()

  const [selectedRoleId, setSelectedRoleId] = useState('')
  const [draft, setDraft] = useState<string[] | null>(null)
  const [newRole, setNewRole] = useState<{ code: string; name: string; description: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<string | null>(null)

  const roles = matrix.data?.roles ?? []
  const role = roles.find((candidate) => candidate.id === selectedRoleId) ?? roles[0]

  // Adjust during render rather than in an effect: the first load has no selection yet.
  if (role && selectedRoleId !== role.id) setSelectedRoleId(role.id)

  const held = useMemo(() => new Set(draft ?? role?.permissions ?? []), [draft, role])
  const dirty = draft != null

  const byModule = matrix.data?.byModule ?? {}
  const permissionsByCode = useMemo(
    () => new Map((matrix.data?.permissions ?? []).map((permission) => [permission.code, permission])),
    [matrix.data],
  )

  const toggle = (code: string) => {
    if (!role?.editable) return
    const next = new Set(held)
    if (next.has(code)) next.delete(code)
    else next.add(code)
    setDraft([...next])
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Roles & permissions"
        subtitle="What each role can do, and which combinations should never sit in one."
        actions={
          <Button
            type="button"
            onClick={() => { setError(null); setNewRole({ code: '', name: '', description: '' }) }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New role
          </Button>
        }
      />

      {banner && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {banner}
        </p>
      )}

      <FilterBar
        actions={
          dirty && role?.editable ? (
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setDraft(null)}>Discard</Button>
              <Button
                type="button"
                disabled={actions.setRolePermissions.isPending}
                onClick={() => {
                  if (!role || !draft) return
                  void (async () => {
                    setBanner(null)
                    try {
                      await actions.setRolePermissions.mutateAsync({
                        roleId: role.id, permissions: draft,
                      })
                      setDraft(null)
                    } catch (caught) {
                      setBanner(toApiProblem(caught)?.detail ?? 'Those permissions could not be saved')
                    }
                  })()
                }}
              >
                Save {draft?.length ?? 0} permission(s)
              </Button>
            </div>
          ) : undefined
        }
      >
        <Select
          aria-label="Role"
          value={role?.id ?? ''}
          onChange={(event) => { setDraft(null); setSelectedRoleId(event.target.value) }}
        >
          {roles.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name} — {candidate.permissions.length}
            </option>
          ))}
        </Select>
      </FilterBar>

      {matrix.isPending && <Skeleton className="h-64" />}
      {matrix.isError && <ErrorState error={matrix.error} onRetry={() => void matrix.refetch()} />}

      {role && !role.editable && role.protectedNote && (
        <p className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
          <Lock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {role.protectedNote}
        </p>
      )}

      {role && role.conflicts.length > 0 && (
        <Card className="border-[var(--color-warning)] p-3">
          <p className="flex items-center gap-2 text-sm font-medium text-[var(--color-heading)]">
            <AlertTriangle className="size-4 text-[var(--color-warning)]" aria-hidden="true" />
            Segregation of duties
          </p>
          <ul className="mt-2 space-y-1 text-sm text-[var(--color-body)]">
            {role.conflicts.map((conflict) => <li key={conflict}>{conflict}</li>)}
          </ul>
          <p className="mt-2 text-xs text-[var(--color-muted)]">
            Held by one role, so anybody with it can do both halves. An override is still
            recorded per person on the segregation-of-duties screen.
          </p>
        </Card>
      )}

      {matrix.isSuccess && Object.keys(byModule).length === 0 && (
        <EmptyState title="No permission catalogue" description="Nothing to show." />
      )}

      {matrix.isSuccess && role && (
        <div className="grid gap-4 md:grid-cols-2">
          {Object.entries(byModule).map(([module, codes]) => (
            <Card key={module} className="p-3">
              <p className="text-sm font-medium capitalize text-[var(--color-heading)]">{module}</p>
              <ul className="mt-2 space-y-1">
                {codes.map((code) => (
                  <li key={code}>
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-0.5 size-4"
                        aria-label={code}
                        checked={held.has(code)}
                        disabled={!role.editable}
                        onChange={() => toggle(code)}
                      />
                      <span>
                        <span className="font-mono text-xs">{code}</span>
                        {permissionsByCode.get(code)?.description && (
                          <span className="block text-xs text-[var(--color-muted)]">
                            {permissionsByCode.get(code)?.description}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}

      <FormDialog
        open={newRole != null}
        onOpenChange={(open) => { if (!open) { setNewRole(null); setError(null) } }}
        title="New role"
        submitLabel="Create"
        loading={actions.createRole.isPending}
        error={error}
        onSubmit={() => {
          if (!newRole) return
          void (async () => {
            setError(null)
            try {
              await actions.createRole.mutateAsync({
                code: newRole.code, name: newRole.name,
                description: newRole.description || null,
              })
              setNewRole(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That role could not be created')
            }
          })()
        }}
      >
        {newRole && (
          <div className="grid gap-3">
            <div>
              <Label htmlFor="role-code">Code</Label>
              <Input
                id="role-code" value={newRole.code} placeholder="SITE_MANAGER"
                onChange={(event) => setNewRole({ ...newRole, code: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Referred to by grants and reports. It cannot be changed afterwards.
              </p>
            </div>
            <div>
              <Label htmlFor="role-name">Name</Label>
              <Input
                id="role-name" value={newRole.name}
                onChange={(event) => setNewRole({ ...newRole, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="role-description">What it is for</Label>
              <Textarea
                id="role-description" rows={3} value={newRole.description}
                onChange={(event) => setNewRole({ ...newRole, description: event.target.value })}
              />
            </div>
            <p className="text-xs text-[var(--color-muted)]">
              A new role starts with no permission at all. Tick what it needs on the matrix.
            </p>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
